import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n/I18nProvider";
import { useAuth } from "../lib/AuthProvider";
import { ChevronDown, Menu, X } from "lucide-react";

const LANGS = [
  { code: "it", label: "IT" },
  { code: "en", label: "EN" },
  { code: "de", label: "DE" },
];

export default function Header({ onBook, onLogin }) {
  const { lang, setLang, t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="logo-link"
            onClick={(e) => {
              if (window.location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <img src="/logo-vico-round.png" alt="Vico's Scooter" className="h-10 w-10 object-contain group-hover:scale-110 transition-transform duration-300" />
            <span className="font-heading font-black text-white tracking-tighter text-xl">VICO<span className="text-scooter-yellow">'S</span> SCOOTER</span>
            <span className="hidden sm:inline text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold ml-1">Alta Badia</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold tracking-wide uppercase">
            <a href="#fleet" className="text-zinc-300 hover:text-scooter-yellow transition-colors" data-testid="nav-fleet">{t.nav.fleet}</a>
            <a href="#routes" className="text-zinc-300 hover:text-scooter-yellow transition-colors" data-testid="nav-routes">{t.nav.routes}</a>
            <a href="#pricing" className="text-zinc-300 hover:text-scooter-yellow transition-colors" data-testid="nav-pricing">{t.nav.pricing}</a>
            <a href="#availability" className="text-zinc-300 hover:text-scooter-yellow transition-colors" data-testid="nav-availability">{t.nav.availability}</a>
            <a href="#gallery" className="text-zinc-300 hover:text-scooter-yellow transition-colors" data-testid="nav-gallery">{t.nav.gallery || "Galleria"}</a>
            <a href="#faq" className="text-zinc-300 hover:text-scooter-yellow transition-colors" data-testid="nav-faq">{t.nav.faq}</a>
          </nav>

          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setLangOpen(v => !v)}
                className="flex items-center gap-1 text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors px-2 py-1"
                data-testid="language-switcher"
              >
                {lang.toUpperCase()} <ChevronDown className="w-3 h-3" />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 bg-[#111] border border-white/10 min-w-[80px]">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`block w-full text-left px-4 py-2 text-xs font-bold tracking-[0.2em] uppercase hover:bg-scooter-yellow hover:text-black ${lang === l.code ? "text-scooter-yellow" : "text-zinc-300"}`}
                      data-testid={`language-option-${l.code}`}
                    >{l.label}</button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <div className="hidden md:flex items-center gap-3">
                {user.role === "admin" && (
                  <button onClick={() => navigate("/admin")} className="text-xs font-bold tracking-[0.2em] uppercase text-scooter-yellow hover:text-scooter-yellow-hover" data-testid="admin-link">{t.nav.admin}</button>
                )}
                <button onClick={() => navigate("/dashboard")} className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-300 hover:text-white" data-testid="dashboard-link">{t.nav.dashboard}</button>
                <button onClick={logout} className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 hover:text-white" data-testid="logout-btn">{t.nav.logout}</button>
              </div>
            ) : (
              <button onClick={onLogin} className="hidden md:inline text-xs font-bold tracking-[0.2em] uppercase text-zinc-300 hover:text-white" data-testid="login-btn">{t.nav.login}</button>
            )}

            <button
              onClick={onBook}
              className="hidden sm:inline-flex items-center bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase px-5 h-10 hover:bg-scooter-yellow-hover transition-colors"
              data-testid="header-book-btn"
            >{t.nav.book}</button>

            <button className="md:hidden text-white p-2" onClick={() => setOpen(v => !v)} data-testid="mobile-menu-btn">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/95 px-4 py-6 flex flex-col gap-4 text-sm">
          <a href="#fleet" onClick={() => setOpen(false)} className="text-zinc-300 uppercase tracking-wider font-bold">{t.nav.fleet}</a>
          <a href="#routes" onClick={() => setOpen(false)} className="text-zinc-300 uppercase tracking-wider font-bold">{t.nav.routes}</a>
          <a href="#pricing" onClick={() => setOpen(false)} className="text-zinc-300 uppercase tracking-wider font-bold">{t.nav.pricing}</a>
          <a href="#availability" onClick={() => setOpen(false)} className="text-zinc-300 uppercase tracking-wider font-bold">{t.nav.availability}</a>
          <a href="#gallery" onClick={() => setOpen(false)} className="text-zinc-300 uppercase tracking-wider font-bold">{t.nav.gallery || "Galleria"}</a>
          <a href="#faq" onClick={() => setOpen(false)} className="text-zinc-300 uppercase tracking-wider font-bold">{t.nav.faq}</a>
          {user ? (
            <>
              {user.role === "admin" && (
                <button onClick={() => { navigate("/admin"); setOpen(false); }} className="text-left text-scooter-yellow uppercase tracking-wider font-bold">{t.nav.admin}</button>
              )}
              <button onClick={() => { navigate("/dashboard"); setOpen(false); }} className="text-left text-zinc-300 uppercase tracking-wider font-bold">{t.nav.dashboard}</button>
              <button onClick={() => { logout(); setOpen(false); }} className="text-left text-zinc-500 uppercase tracking-wider font-bold">{t.nav.logout}</button>
            </>
          ) : (
            <button onClick={() => { onLogin(); setOpen(false); }} className="text-left text-zinc-300 uppercase tracking-wider font-bold">{t.nav.login}</button>
          )}
          <button onClick={() => { onBook(); setOpen(false); }} className="bg-scooter-yellow text-black font-bold uppercase tracking-wider px-5 h-12">{t.nav.book}</button>
        </div>
      )}
    </header>
  );
}
